import React, { useEffect, useState, useMemo } from 'react';
import {
    Card,
    CardBody,
    CardHeader,
    Row,
    Col,
    Button,
    Input,
    Table,
    Badge,
    Spinner,
    InputGroup,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarDay,
    faChevronLeft,
    faChevronRight,
    faRotateRight,
    faPhone,
    faSearch,
    faUsers,
    faCheckCircle,
    faClock,
    faInfoCircle,
    faFilter,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import ReactHTMLTableToExcel from 'react-html-table-to-excel';
import { withTranslation } from 'react-multi-lang';

import EssService from '../Services/EssService';
import UsersService from '../Services/UsersService';
import AuthService from '../Services/AuthService';

const formatDateString = (val) => {
    if (!val) return '';
    if (typeof val === 'string') {
        return val.substring(0, 10);
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatGreekDate = (val) => {
    if (!val) return '';
    if (typeof val === 'string') {
        const parts = val.substring(0, 10).split('-');
        if (parts.length === 3) {
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
};

const getStatusBadge = (status, t) => {
    switch (status?.toLowerCase()) {
        case 'approved':
            return <Badge color="success">{t('phoneCenter.approvedLeaves') || 'Approved'}</Badge>;
        case 'pending':
            return <Badge color="warning">{t('phoneCenter.pendingLeaves') || 'Pending'}</Badge>;
        case 'rejected':
            return <Badge color="danger">Rejected</Badge>;
        case 'cancelled':
            return <Badge color="secondary">Cancelled</Badge>;
        default:
            return <Badge color="info">{status || '-'}</Badge>;
    }
};

const calculateDurationDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 1;
    const s = new Date(formatDateString(startDate));
    const e = new Date(formatDateString(endDate));
    const diffTime = e.getTime() - s.getTime();
    if (isNaN(diffTime) || diffTime < 0) return 1;
    return Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

const PhoneCenter = ({ t }) => {
    const essService = useMemo(() => new EssService(), []);
    const usersService = useMemo(() => new UsersService(), []);
    const authService = useMemo(() => new AuthService(), []);

    // Date state - defaults to today
    const [selectedDate, setSelectedDate] = useState(() => formatDateString(new Date()));

    // Data state
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters & Search
    const [searchTerm, setSearchTerm] = useState('');
    const [warehouseFilter, setWarehouseFilter] = useState('all');
    const [specialtyFilter, setSpecialtyFilter] = useState('all');
    const [companyFilter, setCompanyFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'approved', 'pending'

    // Selected modal
    const [selectedDetail, setSelectedDetail] = useState(null);

    const loadData = () => {
        setLoading(true);
        Promise.all([
            essService.getAllLeaveRequests().catch(() => ({ data: [] })),
            usersService.getUsers().catch(() => [])
        ]).then(([leaveRes, usersRes]) => {
            setLeaveRequests(leaveRes?.data || []);
            setEmployees(usersRes || []);
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    };

    useEffect(() => {
        loadData();
    }, []);

    // Date navigation handlers
    const handlePrevDay = () => {
        const parts = selectedDate.split('-');
        const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
        d.setDate(d.getDate() - 1);
        setSelectedDate(formatDateString(d));
    };

    const handleNextDay = () => {
        const parts = selectedDate.split('-');
        const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
        d.setDate(d.getDate() + 1);
        setSelectedDate(formatDateString(d));
    };

    const handleToday = () => {
        setSelectedDate(formatDateString(new Date()));
    };

    // User lookup map
    const userMap = useMemo(() => {
        const map = {};
        employees.forEach(u => {
            map[u.userId] = u;
        });
        return map;
    }, [employees]);

    // Distinct filter lists
    const allWarehouses = useMemo(() => {
        return [...new Set([
            ...(authService.availableConfigs || []).map(c => c.whCode),
            ...employees.map(e => e.whs).filter(Boolean).flatMap(w => w.split(',').map(s => s.trim()))
        ])].filter(Boolean);
    }, [authService.availableConfigs, employees]);

    const getWarehouseLabel = (whCode) => {
        if (!whCode) return '-';
        const config = (authService.availableConfigs || []).find(c => c.whCode === whCode);
        return config ? `${config.name} (${whCode})` : `Warehouse ${whCode}`;
    };

    const allSpecialties = useMemo(() => {
        return [...new Set(employees.map(e => e.specialty).filter(Boolean))];
    }, [employees]);

    const allCompanies = useMemo(() => {
        return [...new Set(employees.map(e => e.company).filter(Boolean))];
    }, [employees]);

    // Filter leaves for the selected day
    const dayLeaves = useMemo(() => {
        if (!selectedDate) return [];

        return leaveRequests.filter(lr => {
            // Exclude cancelled or rejected requests
            if (lr.status === 'rejected' || lr.status === 'cancelled') {
                return false;
            }

            const startStr = formatDateString(lr.startDate);
            const endStr = formatDateString(lr.endDate);
            if (!startStr || !endStr) return false;

            // Check if selectedDate falls within [startStr, endStr]
            return startStr <= selectedDate && selectedDate <= endStr;
        }).map(lr => {
            const user = userMap[lr.userId] || lr.user || {};
            return {
                ...lr,
                resolvedUser: user,
                employeeName: user.name || lr.user?.name || `User #${lr.userId}`,
                employeeUsername: user.username || lr.user?.username || '',
                employeeSpecialty: user.specialty || lr.user?.specialty || '-',
                employeeWhs: user.whs || lr.user?.whs || '',
                employeeCompany: user.company || lr.user?.company || '-',
                employeeManager: user.manager || lr.user?.manager || '-'
            };
        });
    }, [leaveRequests, selectedDate, userMap]);

    // Filtered list based on search and dropdowns
    const filteredLeaves = useMemo(() => {
        return dayLeaves.filter(item => {
            // Status filter
            if (statusFilter !== 'all' && item.status !== statusFilter) {
                return false;
            }

            // Warehouse filter
            if (warehouseFilter !== 'all') {
                const whs = item.employeeWhs;
                if (!whs) return false;
                const whList = whs.split(',').map(s => s.trim());
                if (!whList.includes(warehouseFilter) && whs !== warehouseFilter) {
                    return false;
                }
            }

            // Specialty filter
            if (specialtyFilter !== 'all' && item.employeeSpecialty !== specialtyFilter) {
                return false;
            }

            // Company filter
            if (companyFilter !== 'all' && item.employeeCompany !== companyFilter) {
                return false;
            }

            // Search filter
            if (searchTerm.trim()) {
                const q = searchTerm.toLowerCase();
                const matchName = item.employeeName.toLowerCase().includes(q);
                const matchUser = item.employeeUsername.toLowerCase().includes(q);
                const matchSpec = item.employeeSpecialty.toLowerCase().includes(q);
                const matchWh = item.employeeWhs.toLowerCase().includes(q);
                const matchComp = item.employeeCompany.toLowerCase().includes(q);
                const matchMgr = item.employeeManager.toLowerCase().includes(q);
                const matchType = (item.leaveType?.name || '').toLowerCase().includes(q);
                const matchReason = (item.reason || '').toLowerCase().includes(q);

                if (!matchName && !matchUser && !matchSpec && !matchWh && !matchComp && !matchMgr && !matchType && !matchReason) {
                    return false;
                }
            }

            return true;
        });
    }, [dayLeaves, statusFilter, warehouseFilter, specialtyFilter, companyFilter, searchTerm]);

    // Statistics counts
    const approvedCount = dayLeaves.filter(l => l.status === 'approved').length;
    const pendingCount = dayLeaves.filter(l => l.status === 'pending').length;
    const isToday = selectedDate === formatDateString(new Date());

    const formatDisplayDate = (dateStr) => {
        if (!dateStr) return '';
        const parts = dateStr.split('-');
        if (parts.length !== 3) return dateStr;
        const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
        return d.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    };

    return (
        <div className="phone-center-container p-3">
            {/* Header Card */}
            <Card className="mb-3 shadow-sm border-0">
                <CardHeader className="bg-white border-bottom py-3">
                    <Row className="align-items-center">
                        <Col md="6" xs="12">
                            <h4 className="mb-0 text-primary d-flex align-items-center">
                                <FontAwesomeIcon icon={faPhone} className="me-2 mr-2" />
                                {t('phoneCenter.title') || 'Phone Center - Daily Leaves'}
                            </h4>
                            <small className="text-muted">
                                {t('phoneCenter.description') || 'View all employees on leave on a specific day'}
                            </small>
                        </Col>
                        <Col md="6" xs="12" className="text-md-end text-start mt-2 mt-md-0 d-flex justify-content-md-end align-items-center flex-wrap gap-2">
                            <ReactHTMLTableToExcel
                                id="phoneCenterExcelBtn"
                                className="btn btn-success btn-sm me-2 mr-2"
                                table="phoneCenterTable"
                                filename={`leaves-${selectedDate}`}
                                sheet="Leaves"
                                buttonText={t('phoneCenter.exportExcel') || 'Export to Excel'}
                            />
                            <Button size="sm" color="outline-primary" onClick={loadData} title="Refresh Data">
                                <FontAwesomeIcon icon={faRotateRight} className={loading ? 'fa-spin' : ''} />
                            </Button>
                        </Col>
                    </Row>
                </CardHeader>

                <CardBody className="bg-light py-3">
                    {/* Date Navigation Bar */}
                    <Row className="align-items-center g-2 justify-content-between">
                        <Col lg="5" md="6" xs="12" className="d-flex align-items-center flex-wrap gap-2">
                            <Button size="sm" color="secondary" outline onClick={handlePrevDay} title={t('phoneCenter.prevDay') || 'Previous Day'}>
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </Button>

                            <Input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="form-control-sm"
                                style={{ width: '160px', display: 'inline-block' }}
                            />

                            <Button size="sm" color="secondary" outline onClick={handleNextDay} title={t('phoneCenter.nextDay') || 'Next Day'}>
                                <FontAwesomeIcon icon={faChevronRight} />
                            </Button>

                            <Button 
                                size="sm" 
                                color={isToday ? "primary" : "outline-secondary"} 
                                onClick={handleToday}
                                className="ms-1 ml-1"
                            >
                                <FontAwesomeIcon icon={faCalendarDay} className="me-1 mr-1" />
                                {t('phoneCenter.today') || 'Today'}
                            </Button>
                        </Col>

                        <Col lg="4" md="6" xs="12" className="text-md-end text-start my-1 my-md-0">
                            <span className="fw-bold text-dark" style={{ fontSize: '1.05rem' }}>
                                {formatDisplayDate(selectedDate)}
                            </span>
                            <div className="small text-muted">
                                ({formatGreekDate(selectedDate)})
                            </div>
                        </Col>

                        {/* Summary Badges */}
                        <Col lg="3" xs="12" className="d-flex justify-content-lg-end align-items-center flex-wrap gap-2">
                            <span className="badge bg-primary p-2" style={{ fontSize: '0.85rem' }}>
                                <FontAwesomeIcon icon={faUsers} className="me-1 mr-1" />
                                {t('phoneCenter.totalOnLeave') || 'Total On Leave'}: <strong>{dayLeaves.length}</strong>
                            </span>
                            <span className="badge bg-success p-2" style={{ fontSize: '0.85rem' }}>
                                <FontAwesomeIcon icon={faCheckCircle} className="me-1 mr-1" />
                                {approvedCount}
                            </span>
                            {pendingCount > 0 && (
                                <span className="badge bg-warning text-dark p-2" style={{ fontSize: '0.85rem' }}>
                                    <FontAwesomeIcon icon={faClock} className="me-1 mr-1" />
                                    {pendingCount}
                                </span>
                            )}
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Filter Controls Card */}
            <Card className="mb-3 shadow-sm border-0">
                <CardBody className="py-2">
                    <Row className="g-2 align-items-center">
                        <Col md="3" xs="12">
                            <InputGroup size="sm">
                                <Input
                                    type="text"
                                    placeholder={t('phoneCenter.searchPlaceholder') || 'Search by employee, specialty, company...'}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <Button color="secondary" outline size="sm" onClick={() => setSearchTerm('')}>
                                        <FontAwesomeIcon icon={faTimes} />
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>

                        <Col md="2" xs="6">
                            <Input
                                type="select"
                                bsSize="sm"
                                value={warehouseFilter}
                                onChange={(e) => setWarehouseFilter(e.target.value)}
                            >
                                <option value="all">{t('phoneCenter.allWarehouses') || 'All Facilities'}</option>
                                {allWarehouses.map(w => (
                                    <option key={w} value={w}>{getWarehouseLabel(w)}</option>
                                ))}
                            </Input>
                        </Col>

                        <Col md="2" xs="6">
                            <Input
                                type="select"
                                bsSize="sm"
                                value={specialtyFilter}
                                onChange={(e) => setSpecialtyFilter(e.target.value)}
                            >
                                <option value="all">{t('phoneCenter.allSpecialties') || 'All Specialties'}</option>
                                {allSpecialties.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </Input>
                        </Col>

                        <Col md="2" xs="6">
                            <Input
                                type="select"
                                bsSize="sm"
                                value={companyFilter}
                                onChange={(e) => setCompanyFilter(e.target.value)}
                            >
                                <option value="all">{t('phoneCenter.allCompanies') || 'All Companies'}</option>
                                {allCompanies.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </Input>
                        </Col>

                        <Col md="2" xs="6">
                            <Input
                                type="select"
                                bsSize="sm"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="all">{t('phoneCenter.statusAll') || 'All Statuses'}</option>
                                <option value="approved">{t('phoneCenter.statusApproved') || 'Approved Only'}</option>
                                <option value="pending">{t('phoneCenter.statusPending') || 'Pending Only'}</option>
                            </Input>
                        </Col>

                        <Col md="1" xs="12" className="text-end">
                            {(searchTerm || warehouseFilter !== 'all' || specialtyFilter !== 'all' || companyFilter !== 'all' || statusFilter !== 'all') && (
                                <Button 
                                    size="sm" 
                                    color="link" 
                                    className="p-0 text-muted"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setWarehouseFilter('all');
                                        setSpecialtyFilter('all');
                                        setCompanyFilter('all');
                                        setStatusFilter('all');
                                    }}
                                >
                                    <FontAwesomeIcon icon={faRotateRight} className="me-1 mr-1" />
                                    Clear
                                </Button>
                            )}
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Results Table Card */}
            <Card className="shadow-sm border-0">
                <CardBody className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">Loading leaves...</div>
                        </div>
                    ) : filteredLeaves.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <FontAwesomeIcon icon={faCalendarDay} size="3x" className="mb-3 text-secondary opacity-50" />
                            <h5>{t('phoneCenter.noLeavesFound') || 'No employees on leave for the selected day'}</h5>
                            <p className="small">
                                {dayLeaves.length > 0 ? 'No leaves match current filter criteria.' : `No approved or pending leaves active on ${formatGreekDate(selectedDate)}.`}
                            </p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover striped className="mb-0 align-middle" id="phoneCenterTable">
                                <thead className="bg-light table-light">
                                    <tr>
                                        <th style={{ width: '22%' }}>{t('phoneCenter.employee') || 'Employee'}</th>
                                        <th style={{ width: '15%' }}>{t('phoneCenter.specialty') || 'Specialty'}</th>
                                        <th style={{ width: '13%' }}>{t('phoneCenter.facility') || 'Facility'}</th>
                                        <th style={{ width: '13%' }}>{t('phoneCenter.company') || 'Company'}</th>
                                        <th style={{ width: '15%' }}>{t('phoneCenter.leaveType') || 'Leave Type'}</th>
                                        <th style={{ width: '12%' }}>{t('phoneCenter.leavePeriod') || 'Period'}</th>
                                        <th style={{ width: '10%' }} className="text-center">{t('phoneCenter.status') || 'Status'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredLeaves.map(item => {
                                        const duration = calculateDurationDays(item.startDate, item.endDate);
                                        return (
                                            <tr 
                                                key={item.id} 
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => setSelectedDetail(item)}
                                                title="Click to view leave details"
                                            >
                                                <td>
                                                    <div className="fw-bold text-dark">{item.employeeName}</div>
                                                    {item.employeeUsername && (
                                                        <small className="text-muted">@{item.employeeUsername}</small>
                                                    )}
                                                </td>
                                                <td>
                                                    <span>{item.employeeSpecialty}</span>
                                                </td>
                                                <td>
                                                    <span>{getWarehouseLabel(item.employeeWhs)}</span>
                                                </td>
                                                <td>
                                                    <div>{item.employeeCompany}</div>
                                                    {item.employeeManager && item.employeeManager !== '-' && (
                                                        <small className="text-muted">Mgr: {item.employeeManager}</small>
                                                    )}
                                                </td>
                                                <td>
                                                    <Badge color="info" className="p-1 px-2 text-wrap text-start">
                                                        {item.leaveType?.name || 'General Leave'}
                                                    </Badge>
                                                    {item.reason && (
                                                        <div className="small text-muted text-truncate" style={{ maxWidth: '180px' }} title={item.reason}>
                                                            {item.reason}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="small fw-semibold">
                                                        {formatGreekDate(item.startDate)} - {formatGreekDate(item.endDate)}
                                                    </div>
                                                    <small className="text-muted">
                                                        {duration} {duration === 1 ? 'day' : 'days'}
                                                    </small>
                                                </td>
                                                <td className="text-center">
                                                    {getStatusBadge(item.status, t)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Leave Details Modal */}
            <Modal isOpen={!!selectedDetail} toggle={() => setSelectedDetail(null)} centered>
                <ModalHeader toggle={() => setSelectedDetail(null)}>
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2 mr-2 text-primary" />
                    Leave Request Details
                </ModalHeader>
                <ModalBody>
                    {selectedDetail && (
                        <div>
                            <Row className="mb-2">
                                <Col sm="4" className="text-muted fw-bold">Employee:</Col>
                                <Col sm="8">
                                    <strong>{selectedDetail.employeeName}</strong>
                                    {selectedDetail.employeeUsername && (
                                        <div className="small text-muted">@{selectedDetail.employeeUsername}</div>
                                    )}
                                </Col>
                            </Row>
                            <Row className="mb-2">
                                <Col sm="4" className="text-muted fw-bold">Specialty:</Col>
                                <Col sm="8">{selectedDetail.employeeSpecialty}</Col>
                            </Row>
                            <Row className="mb-2">
                                <Col sm="4" className="text-muted fw-bold">Facility:</Col>
                                <Col sm="8">{getWarehouseLabel(selectedDetail.employeeWhs)}</Col>
                            </Row>
                            <Row className="mb-2">
                                <Col sm="4" className="text-muted fw-bold">Company / Mgr:</Col>
                                <Col sm="8">{selectedDetail.employeeCompany} / {selectedDetail.employeeManager}</Col>
                            </Row>
                            <hr />
                            <Row className="mb-2">
                                <Col sm="4" className="text-muted fw-bold">Leave Type:</Col>
                                <Col sm="8">
                                    <Badge color="info">
                                        {selectedDetail.leaveType?.name || 'General Leave'}
                                    </Badge>
                                </Col>
                            </Row>
                            <Row className="mb-2">
                                <Col sm="4" className="text-muted fw-bold">Period:</Col>
                                <Col sm="8">
                                    {formatGreekDate(selectedDetail.startDate)} - {formatGreekDate(selectedDetail.endDate)}
                                    <span className="ms-2 ml-2 badge bg-light text-dark">
                                        {calculateDurationDays(selectedDetail.startDate, selectedDetail.endDate)} days
                                    </span>
                                </Col>
                            </Row>
                            <Row className="mb-2">
                                <Col sm="4" className="text-muted fw-bold">Status:</Col>
                                <Col sm="8">{getStatusBadge(selectedDetail.status, t)}</Col>
                            </Row>
                            {selectedDetail.reason && (
                                <Row className="mb-2">
                                    <Col sm="4" className="text-muted fw-bold">Reason:</Col>
                                    <Col sm="8">{selectedDetail.reason}</Col>
                                </Row>
                            )}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setSelectedDetail(null)}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default withTranslation(PhoneCenter);
